import {useTranslation} from "@unibuddy/intl";
import {Box, Text, VisuallyHidden} from "@unibuddy/patron";
import React, {useEffect} from "react";
import {isEmpty, isNil} from "lodash";
import {Redirect, useLocation} from "react-router-dom";
import {differenceInDays, parseISO} from "date-fns";

import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import PageTitle from "shared/Shared/components/PageTitle/PageTitle";
import {
    DecisionStagePageTitle,
    DecisionStagePageSubtitle,
    ProspectStageLocation,
    useGetProspectStageQuery,
} from "../../Chat/components/PopupQuestions/prospectStages";
import {DecisionStageFormContainer} from "../components/DecisionStageForm";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {useSearchQuery} from "../../../../hooks/useSearchQuery";

const RESHOW_DECISION_STAGE_DAYS = 2;

export function DecisionStagePage() {
    const location = useLocation();
    const {trackEvent} = useAnalytics();
    const university = useUniversity();
    const searchQuery = useSearchQuery();
    const template = searchQuery.get("template");
    const channel = searchQuery.get("channel");
    const {t, ready} = useTranslation("decisionStage");
    const prospectStageResponse = useGetProspectStageQuery(university.id);

    useEffect(() => {
        trackEvent(sharedEvents.prospectDecisionStagesShown.name, {
            universityId: university.id,
            universityName: university.name,
            where: ProspectStageLocation.WIDGET_DECISION_PAGE,
            templateLinkedFrom: template ?? "unknown",
            channel: channel ?? "unknown",
        });
    }, [university, trackEvent, template, channel]);

    if (!ready || prospectStageResponse.loading) return <p>Loading...</p>;

    // TODO: Upgrade PageTitle component to tsx so title can be passed directly
    const pageTitleProps = {
        title: t(DecisionStagePageTitle.translationKey, DecisionStagePageTitle.name),
    };

    if (!isEmpty(template) && !isNil(prospectStageResponse.data)) {
        const updated = prospectStageResponse.data.currentUserDecisionStage.updated;
        /* If a user has submitted a decision stage within `RESHOW_DECISION_STAGE_DAYS`
         * and are accessing this page via an email then redirect to their recommendations.
         */
        if (
            updated &&
            differenceInDays(parseISO(updated), new Date()) <= RESHOW_DECISION_STAGE_DAYS
        ) {
            return (
                <Redirect
                    to={`${location.pathname.replace("/decision-stage", "/recommendations")}${
                        location.search
                    }`}
                />
            );
        }
    }

    return (
        <Box>
            <VisuallyHidden>
                <PageTitle {...pageTitleProps} />
            </VisuallyHidden>
            <Box paddingBottom="medium" paddingTop="xxsmall">
                <Text size="large">
                    {t(DecisionStagePageSubtitle.translationKey, DecisionStagePageSubtitle.name)}
                </Text>
            </Box>
            <DecisionStageFormContainer
                decisionStageLocation={ProspectStageLocation.WIDGET_DECISION_PAGE}
            />
        </Box>
    );
}
